import { 
  Modal, 
  ModalCloseButton, 
  ModalBody, 
  ModalContent,  
  ModalOverlay,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Grid,
  HStack,
  Divider,
  Text,
  Box,
  GridItem,
  Spinner,
  SimpleGrid,
  Button
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { Header } from "build-ts/metadata";
import { useAnalysisDrawerContext } from "context/AnalysisDrawerContext";
import showToast from "hooks/useCustomToast";
import { FaCheckCircle } from "react-icons/fa";
import { RiSearchLine } from "react-icons/ri";
import { getTreeHeaders } from "services/AnalysisTreeServices";

const AddFromLibraryModal = ({isOpen, onClose}: {isOpen: boolean, onClose: () => void}) => {

  const [treesHeaders, setTreesHeaders] = useState<Header[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [selectedTreeHeaders, setSelectedTreeHeaders] = useState<Header[]>([]);
  const {setTreeIds, responseData} = useAnalysisDrawerContext();

  const getTrees = async (searchString: string) => {
    setIsLoading(true);
    try{
      const response = await getTreeHeaders(
        "LAST_MODIFIED",
        [],
        -1,
        1,
        searchString
      );
      if(response && response?.headers?.length > 0){
        setTreesHeaders(response?.headers || []);
        
        // Pre-select trees that are already present in responseData
        const presentHeaders = response?.headers?.filter((header: Header) => {
          return Object.entries(responseData).some(([_, treeMap]) => 
            treeMap.tree?.header?.id === header.id
          );
        });
        
        setSelectedTreeHeaders(presentHeaders);
      }
    } catch (err) {
      showToast("Error in getting tree", "", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const isSelected = (treeId: string) => {
    return (
      selectedTreeHeaders &&
      selectedTreeHeaders.some((header) => header.id === treeId)
    );
  };

  // Handle tree header selection
  const handleTreeHeaderSelection = (header: Header) => {
    if (!selectedTreeHeaders.some((selected) => selected.id === header.id)) {
      setSelectedTreeHeaders([...selectedTreeHeaders, header]);
    } else {
      setSelectedTreeHeaders(
        selectedTreeHeaders.filter((selected) => selected.id !== header.id)
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if(e.key === "Enter"){
      getTrees(searchString);
    }
  };

  const handleAddTrees = () => {
    onClose();
    const selectedTreeIds = selectedTreeHeaders.map(tree => tree.id).filter((id): id is string => id !== undefined);
    const presentTreeIds: string[] = [];
    Object.entries(responseData).forEach(([transactionId, treeMap]) => {
        if (treeMap.tree?.header?.id) {
          presentTreeIds.push(treeMap.tree.header.id);
        }
    });
    const newTreeIds = selectedTreeIds.filter(id => !presentTreeIds.includes(id));

    setTreeIds((prevTreeIds) => [...prevTreeIds, ...newTreeIds]);
    setSelectedTreeHeaders([]);
  };

  useEffect(() => {
    if(isOpen){
      getTrees(searchString);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="xl">
      <ModalOverlay />
      <ModalContent maxH="80vh" w="90%" maxW="800px">
        <Flex px={6} py={4} alignItems="center" position="relative">
          <Text fontSize="xl" fontWeight="bold">Add from Library</Text>
          <Flex position="absolute" right={12} gap={4} alignItems="center">
            <Button colorScheme="purple" size="sm" onClick={handleAddTrees}>
              {selectedTreeHeaders.length === 0 
                ? "Add Tree" 
                : `Add ${selectedTreeHeaders.length} ${selectedTreeHeaders.length === 1 ? "Tree" : "Trees"}`}
            </Button>
            <ModalCloseButton position="static" />
          </Flex>
        </Flex>
        <ModalBody pb={6} overflowY="auto">
          <Grid>
            <GridItem>
              <Flex align="center">
                <InputGroup size="sm">
                  <InputLeftElement>
                    <Icon as={RiSearchLine} color="gray.500" fontSize="md" />
                  </InputLeftElement>
                  <Input
                    focusBorderColor="purple.600"
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                    onKeyDown={handleKeyDown}
                    width="full"
                    fontSize="sm"
                    variant="filled"
                    type="text"
                    placeholder="Search..."
                    autoComplete="off"
                  />
                </InputGroup>
              </Flex>
              <HStack bg="bg.surface">
                <Divider m={0} />
                <Text
                  fontSize="xs"
                  color="fg.muted"
                  whiteSpace="nowrap"
                  fontWeight={600}
                  my={0}
                  mx={1}
                >
                  Showing {treesHeaders?.length ?? 0} results
                </Text>
                <Divider m={0} />
              </HStack>
            </GridItem>
            <GridItem>
              {isLoading ? (
                <Flex p={3} height="50vh" justifyContent="center" alignItems="center" width="100%">
                  <Spinner size="xl" />
                </Flex>
              ) : treesHeaders && treesHeaders.length > 0 ? (
                <SimpleGrid minChildWidth="250px" gap={2} maxH="50vh" overflowY="auto" p={2}>
                  {treesHeaders.map((header) => (
                    <Box
                    key={header.id}
                    p={3}
                    pb={2}
                    boxShadow="md"
                    borderWidth={isSelected(header.id!) ? "2px" : "2px"}
                    borderRadius="lg"
                    bg={isSelected(header.id!) ? "bg.surface" : "bg.surface"}
                    borderColor={isSelected(header.id!) ? "purple.700" : "gray.50"}
                    overflowY="auto"
                    display="flex"
                    flexDirection="column"
                    position="relative"
                    height="100%"
                    _hover={{
                      bg: isSelected(header.id!) ? "green.50" : "gray.50",
                    }}
                    role="group"
                    onClick={() => handleTreeHeaderSelection(header)}
                    cursor="pointer"
                  >
                    <Box
                      flex="1"
                      display="flex"
                      flexDirection="column"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Text
                          fontWeight={600}
                          fontSize="sm"
                          mb={2}
                          noOfLines={2}
                          align="left"
                        >
                          {header.name}
                        </Text>
                      </Box>
                      <Flex align="center" justifyContent="flex-end">
                        <HStack spacing="1" visibility={isSelected(header.id!) ? "visible" : "hidden"}>
                          <Icon
                            as={FaCheckCircle}
                            color="green.500"
                            boxSize={3}
                            m={0}
                          />
                          <Text
                            m={0}
                            fontSize="xs"
                            fontWeight="bold"
                            color="green.500"
                          >
                            Selected
                          </Text>
                        </HStack>
                        {/* Invisible placeholder to reserve space */}
                        {!isSelected(header.id!) && (
                          <HStack spacing="1" visibility="hidden" position="absolute">
                            <Icon as={FaCheckCircle} boxSize={3} m={0} />
                            <Text m={0} fontSize="xs" fontWeight="bold">Selected</Text>
                          </HStack>
                        )}
                      </Flex>
                    </Box>
                  </Box>
                  ))}
                </SimpleGrid>
              ) : (
                <Flex p={3} height="50vh" justifyContent="center" alignItems="center" width="100%">
                  <Text fontSize="lg" color="gray.500">
                    No results found. Please modify your search.
                  </Text>
                </Flex>
              )}
            </GridItem>
          </Grid>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddFromLibraryModal;